<!--
    The delivery filter, as a visible row of tiles.

    Not a dropdown. This menu is used on a tablet at the rack, where a control
    you have to open is a control you cannot see: one tap filters, a second tap
    on the active tile clears it, and nothing overlays anything.

    It also replaces the prev/next arrows the production viewer's batch selector
    has. Those exist because a project holds hundreds of batches; there are
    nineteen rack loads in the entire database, so the list *is* the navigation.

    Three tiles, because a rack has only two jobs left on it: getting the
    panels onto it, and getting it out of the door. They partition the
    unfinished work, and everything finished or not yet started shows under All.
    A load past its date still stands out on its own row, in red.
-->
<template>
    <v-card variant="flat" class="my-2 border-thin w-100">
        <div class="fgrid">
            <div v-for="f in filters" :key="f.value" class="ftile"
                :class="{ on: active === f.value, empty: !f.count }"
                @click="pick(f.value)">
                <span class="n pit-mono">{{ f.count }}</span>
                <span class="l">{{ t(f.key) }}</span>
            </div>
        </div>
    </v-card>
</template>

<script setup>
import { computed } from "vue";
import { usePitStoreBasics } from "@/stores/app_common";
import { useLogisticsStore } from "@/stores/logistics";
import {
    RACK_LOAD_FILTERS,
    rackLoadMatchesFilter,
} from "@/components/delivery/delivery_composables";

const { t } = usePitStoreBasics();
const logistics_store = useLogisticsStore();

const active = computed(() => logistics_store.delivery_filter);

// Counted over the whole list, not over the filtered one: a tile has to say
// how many loads it would show, including while another tile is active.
const filters = computed(() => RACK_LOAD_FILTERS.map(f => ({
    ...f,
    count: logistics_store.rack_traces.filter(
        trace => rackLoadMatchesFilter(trace, f.value)).length,
})));

const pick = (value) => logistics_store.setDeliveryFilter(value);
</script>

<style scoped>
.fgrid {
    display: flex;
    flex-wrap: wrap;
}

/* Three per row, two rows. In the 400 px drawer that is about 105 px per tile,
   enough for "Delivered" at this size. 46 px tall, which with the count and
   the label above the 48 px touch floor as a whole cell. */
.ftile {
    flex: 0 0 33.333%;
    min-width: 0;
    height: 46px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: rgba(var(--v-theme-on-surface), 0.6);
    border-right: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    user-select: none;
}

.ftile:last-child {
    border-right: none;
}

.ftile .n {
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.1;
    color: rgba(var(--v-theme-on-surface), 0.85);
}

.ftile .l {
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    padding: 0 2px;
}

.ftile.on {
    background: rgba(var(--v-theme-accent), 0.1);
    color: rgb(var(--v-theme-accent));
}

.ftile.on .n {
    color: rgb(var(--v-theme-accent));
}

.ftile.empty .n,
.ftile.empty .l {
    color: rgba(var(--v-theme-on-surface), 0.3);
}
</style>
